import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/cgjm5igj_FB%20Cover%20logo.png";

export default function Register() {
  usePageTitle("Create Account");
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", referred_by: params.get("ref") || "", newsletter_opt_in: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register({ ...form, role: "client" });
      toast.success("Account created!");
      navigate("/portal");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#F5F5F4]">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E7E5E4] shadow-sm p-8">
        <Link to="/" className="flex justify-center mb-6"><img src={LOGO} alt="MBBC" className="h-14 w-14 object-contain" /></Link>
        <h2 className="font-heading text-2xl text-navy text-center">Create your account</h2>
        <p className="mt-1 text-center text-sm text-[#57534E]">Already a member? <Link to="/login" className="text-teal font-medium">Log in</Link></p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <input data-testid="register-name" required placeholder="Full name" value={form.name} onChange={set("name")}
            className="w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
          <input data-testid="register-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")}
            className="w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
          <input data-testid="register-password" type="password" required minLength={6} placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")}
            className="w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
          <input data-testid="register-referral" placeholder="Therapist referral code (optional)" value={form.referred_by} onChange={set("referred_by")}
            className="w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input data-testid="register-newsletter" type="checkbox" checked={form.newsletter_opt_in}
              onChange={(e) => setForm({ ...form, newsletter_opt_in: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-[#D6D3D1] text-teal accent-[#0d9488]" />
            <span className="text-xs text-[#57534E]">Keep me updated with MBBC news, recipes and metabolic health tips. Unsubscribe anytime.</span>
          </label>
          {err && <p data-testid="register-error" className="text-sm text-red-600">{err}</p>}
          <button data-testid="register-submit" disabled={busy} type="submit"
            className="w-full bg-navy text-white hover:bg-navy-dark transition-colors rounded-lg py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create account
          </button>
        </form>

        <p className="mt-5 text-center text-xs text-[#78716C]">
          Want to become a therapist? <Link to="/therapists" className="text-teal font-medium">Register your interest</Link>
        </p>
      </div>
    </div>
  );
}
